import { createRequestCorrelationId } from "@/lib/analytics/correlation";
import { consumeEngagementTimeMsec } from "@/lib/analytics/engagementTime";
import {
  type OverlayCloseReason,
  type WebAnalyticsClientContext,
  type WebAnalyticsEventName,
  type WebAnalyticsEventOfName,
  type WebAnalyticsEventProperties,
} from "@/lib/analytics/types";
import { getMarketingSessionId } from "@/lib/analytics/marketingSession";
import { enqueueMarketingEvent } from "@/lib/analytics/marketingEventOutbox";
import { getPersistedLastTouchAttribution } from "@/lib/analytics/attributionCookies";
import { markVisitActivity } from "@/lib/analytics/visitActivity";
import {
  getAnalyticsLocation,
  getAnalyticsReferrer,
  getGoogleAnalyticsLocation,
} from "@/lib/analytics/urlSanitization";
import { parseAnalyticsElementId, type AnalyticsElementId } from "@/lib/analytics/analyticsElementId";

type TrackedElementEventArgs = {
  elementId: AnalyticsElementId;
};

type PreparedElementEvent = {
  properties: WebAnalyticsEventProperties<"fe_element_clicked">;
};

type UserSignInStartedProperties = WebAnalyticsEventProperties<"user_sign_in_started">;

type ClosedElementEventArgs = TrackedElementEventArgs & {
  reason: OverlayCloseReason;
};

type PageViewArgs = {
  url: string;
  referrer?: string;
  siteOrigin?: string;
};

const CLIENT_CONTEXT_STRING_MAX_LENGTH = {
  language: 32,
  timezone: 64,
  platform: 64,
} as const;
const CLIENT_CONTEXT_DIMENSION_MAX = 32_768;
const CLIENT_CONTEXT_COLOR_DEPTH_MAX = 256;
const CLIENT_CONTEXT_DEVICE_PIXEL_RATIO_MIN = 0.1;
const CLIENT_CONTEXT_DEVICE_PIXEL_RATIO_MAX = 16;

function getClientContext(): WebAnalyticsClientContext | undefined {
  if (typeof window === "undefined" || typeof navigator === "undefined") return undefined;

  const primaryLanguage = readBrowserValue(() => navigator.language);
  const fallbackLanguage = readBrowserValue(() => navigator.languages?.[0]);
  const language = normalizeClientContextString(
    primaryLanguage || fallbackLanguage,
    CLIENT_CONTEXT_STRING_MAX_LENGTH.language,
  );
  const screenWidth = normalizeClientContextInteger(
    readBrowserValue(() => window.screen?.width),
    CLIENT_CONTEXT_DIMENSION_MAX,
  );
  const screenHeight = normalizeClientContextInteger(
    readBrowserValue(() => window.screen?.height),
    CLIENT_CONTEXT_DIMENSION_MAX,
  );
  const viewportWidth = normalizeClientContextInteger(
    readBrowserValue(() => window.innerWidth),
    CLIENT_CONTEXT_DIMENSION_MAX,
  );
  const viewportHeight = normalizeClientContextInteger(
    readBrowserValue(() => window.innerHeight),
    CLIENT_CONTEXT_DIMENSION_MAX,
  );
  const colorDepth = normalizeClientContextInteger(
    readBrowserValue(() => window.screen?.colorDepth),
    CLIENT_CONTEXT_COLOR_DEPTH_MAX,
  );
  const devicePixelRatio = normalizeClientContextNumber(
    readBrowserValue(() => window.devicePixelRatio),
    CLIENT_CONTEXT_DEVICE_PIXEL_RATIO_MIN,
    CLIENT_CONTEXT_DEVICE_PIXEL_RATIO_MAX,
  );
  const timezone = normalizeClientContextString(
    readBrowserValue(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    CLIENT_CONTEXT_STRING_MAX_LENGTH.timezone,
  );
  const platform = normalizeClientContextString(
    readBrowserValue(() => navigator.platform),
    CLIENT_CONTEXT_STRING_MAX_LENGTH.platform,
  );
  // Reading resets the accumulator, so this must happen exactly once per enqueued envelope —
  // the same foreground milliseconds must never be attributed to two events.
  const engagementTimeMsec = readBrowserValue(consumeEngagementTimeMsec);

  return {
    ...(language ? { language } : {}),
    ...(screenWidth !== undefined ? { screenWidth } : {}),
    ...(screenHeight !== undefined ? { screenHeight } : {}),
    ...(viewportWidth !== undefined ? { viewportWidth } : {}),
    ...(viewportHeight !== undefined ? { viewportHeight } : {}),
    ...(colorDepth !== undefined ? { colorDepth } : {}),
    ...(devicePixelRatio !== undefined ? { devicePixelRatio } : {}),
    ...(timezone ? { timezone } : {}),
    ...(platform ? { platform } : {}),
    ...(engagementTimeMsec !== undefined ? { engagementTimeMsec } : {}),
  };
}

function readBrowserValue<Value>(read: () => Value): Value | undefined {
  try {
    return read();
  } catch {
    return undefined;
  }
}

function normalizeClientContextString(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = [...value]
    .filter((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint > 0x1f && (codePoint < 0x7f || codePoint > 0x9f);
    })
    .join("")
    .trim()
    .slice(0, maxLength);
  return normalized || undefined;
}

function normalizeClientContextInteger(value: unknown, maximum: number): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= maximum ? value : undefined;
}

function normalizeClientContextNumber(value: unknown, minimum: number, maximum: number): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= minimum && value <= maximum
    ? value
    : undefined;
}

/**
 * Creates the strict first-party envelope and synchronously puts it in the bounded browser queue.
 * Named helpers below are the only public event surface; feature code cannot nominate identity,
 * timestamps, ids, or attribution.
 */
const enqueueFrontendEvent = <Event extends WebAnalyticsEventName>(
  eventName: Event,
  properties: WebAnalyticsEventProperties<Event>,
): void => {
  const eventId = createRequestCorrelationId();
  const occurredAt = new Date().toISOString();
  const marketingSessionId = getMarketingSessionId();
  const attributionCaptureKey = getPersistedLastTouchAttribution()?.captureKey;
  const clientContext = getClientContext();
  const envelope = {
    eventId,
    marketingSessionId,
    occurredAt,
    ...(attributionCaptureKey ? { attributionCaptureKey } : {}),
    ...(clientContext && Object.keys(clientContext).length > 0 ? { clientContext } : {}),
    eventName,
    properties,
  } as WebAnalyticsEventOfName<Event>;
  enqueueMarketingEvent(envelope);
  markVisitActivity();
};

const prepareElementEvent = ({ elementId }: TrackedElementEventArgs): PreparedElementEvent | undefined => {
  const parsed = parseAnalyticsElementId(elementId);
  if (!parsed) return undefined;

  return {
    properties: {
      element_id: parsed.elementId,
      surface: parsed.surface,
    },
  };
};

const trackElementEvent = (
  eventName: "fe_element_clicked" | "fe_modal_opened" | "fe_sheet_opened",
  args: TrackedElementEventArgs,
): void => {
  const prepared = prepareElementEvent(args);
  if (!prepared) return;

  enqueueFrontendEvent(eventName, prepared.properties);
};

const trackElementClosedEvent = (
  eventName: "fe_modal_closed" | "fe_sheet_closed",
  { reason, ...args }: ClosedElementEventArgs,
): void => {
  const prepared = prepareElementEvent(args);
  if (!prepared) return;

  enqueueFrontendEvent(eventName, { ...prepared.properties, reason });
};

export function trackElementClicked(args: TrackedElementEventArgs): void {
  trackElementEvent("fe_element_clicked", args);
}

export function trackModalOpened(args: TrackedElementEventArgs): void {
  trackElementEvent("fe_modal_opened", args);
}

export function trackModalClosed(args: ClosedElementEventArgs): void {
  trackElementClosedEvent("fe_modal_closed", args);
}

export function trackSheetOpened(args: TrackedElementEventArgs): void {
  trackElementEvent("fe_sheet_opened", args);
}

export function trackSheetClosed(args: ClosedElementEventArgs): void {
  trackElementClosedEvent("fe_sheet_closed", args);
}

export function trackPageView({ url, referrer, siteOrigin }: PageViewArgs): void {
  const base = siteOrigin ?? (typeof window !== "undefined" ? window.location.origin : undefined);
  const location = getAnalyticsLocation(url, base);
  const pageLocation = getGoogleAnalyticsLocation(url, base);
  if (!location || !pageLocation) return;

  const pageReferrer = referrer && base ? getAnalyticsReferrer(referrer, base) : undefined;
  enqueueFrontendEvent("page_view", {
    page_location: pageLocation,
    ...(pageReferrer ? { page_referrer: pageReferrer } : {}),
    // Route templates are the privacy-safe title contract; never forward a raw document title.
    page_title: location.path,
  });
}

export function trackUserSignInStarted({ method, surface }: UserSignInStartedProperties): void {
  enqueueFrontendEvent("user_sign_in_started", { method, ...(surface ? { surface } : {}) });
}
